import { useEffect } from 'react';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import {
  ReanimatedLogLevel,
  configureReanimatedLogger,
} from 'react-native-reanimated';
import '@/common/components/Icons/library';
import { NotificationsProvider } from '@/common/contexts/notifications/NotificationsContext';
import RootLayout from '@/common/pages/_layout';
import '@/common/theme/tv.config';
import '@expo/match-media';
import { ActionSheetProvider } from '@expo/react-native-action-sheet';
import { PortalHost } from '@rn-primitives/portal';
import * as Sentry from '@sentry/react-native';
import { isRunningInExpoGo } from 'expo';
import { useFonts } from 'expo-font';
import { useNavigationContainerRef } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import '@/global.css';

configureReanimatedLogger({
  level: ReanimatedLogLevel.error,
});

SplashScreen.preventAutoHideAsync();

const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: !isRunningInExpoGo(),
});

if (process.env.NODE_ENV !== 'development') {
  Sentry.init({
    dsn: process.env.EXPO_PUBLIC_SENTRY_DSN,
    tracesSampleRate: 0.1,
    integrations: [navigationIntegration],
    enableNativeFramesTracking: !isRunningInExpoGo(), // Tracks slow and frozen frames in the application
  });
}

function Layout() {
  const ref = useNavigationContainerRef();
  const [loaded, error] = useFonts({
    'F37GingerPro-Regular': require('@/assets/fonts/F37GingerPro-Regular.ttf'),
    'F37GingerPro-Bold': require('@/assets/fonts/F37GingerPro-Bold.ttf'),
    'F37GingerPro-Light': require('@/assets/fonts/F37GingerPro-Light.ttf'),
  });

  useEffect(() => {
    if (ref?.current) {
      navigationIntegration.registerNavigationContainer(ref);
    }
  }, [ref]);

  if (!loaded && !error) {
    return null;
  }

  return (
    <NotificationsProvider>
      <ActionSheetProvider>
        <GestureHandlerRootView>
          <RootLayout />
          <PortalHost name="root" />
        </GestureHandlerRootView>
      </ActionSheetProvider>
    </NotificationsProvider>
  );
}

const WrappedLayout =
  process.env.NODE_ENV !== 'development' ? Sentry.wrap(Layout) : Layout;

export default WrappedLayout;
